import React from "react";
import { useDashTheme } from "../../../hooks/useDashTheme";

/* Shared pieces for the "Day Story" presentation of Work Summary (new).
   ADDITIVE: everything lives under story/ and is imported only by
   WorkSummaryNewV2. The older workSummaryNew components stay untouched.

   Wording contract (same as the rest of this surface): nothing here may say
   screen / screenshot / OCR / keystroke — it is always "activity". */

/* ── One <style> tag for the rules inline styles cannot express ────────── */
export function StoryStyles() {
  return (
    <style>{`
      @keyframes st-shimmer { 0% { background-position: 180% 0 } 100% { background-position: -80% 0 } }
      .st-sk {
        background-color: var(--st-sk);
        background-image: linear-gradient(100deg,
          var(--st-sk) 0%, var(--st-sk) 38%, var(--st-sk-hi) 50%, var(--st-sk) 62%, var(--st-sk) 100%);
        background-size: 200% 100%;
        animation: st-shimmer 1.4s linear infinite;
        border-radius: 8px;
      }
      /* Compat: AppDetail (shared with the drawer) still uses the old page's
         shimmer class name — without this rule its skeletons render blank. */
      .wsn-shimmer {
        background-color: var(--wsn-sk, var(--st-sk));
        background-image: linear-gradient(100deg,
          var(--wsn-sk, var(--st-sk)) 0%, var(--wsn-sk, var(--st-sk)) 38%,
          var(--wsn-sk-hi, var(--st-sk-hi)) 50%,
          var(--wsn-sk, var(--st-sk)) 62%, var(--wsn-sk, var(--st-sk)) 100%);
        background-size: 200% 100%;
        animation: st-shimmer 1.4s linear infinite;
      }
      @keyframes st-rise { from { opacity: 0; transform: translateY(12px) } to { opacity: 1; transform: none } }
      .st-rise { animation: st-rise .5s cubic-bezier(.16,1,.3,1) both }

      .st-card-hover { transition: transform .22s cubic-bezier(.16,1,.3,1), box-shadow .22s cubic-bezier(.16,1,.3,1), border-color .22s ease }
      .st-card-hover:hover { transform: translateY(-3px) }

      /* One hover language for every clickable surface on this page.
         Tint + ring — no lift, so nested tiles stay inside the section card. */
      .st-hit {
        cursor: pointer;
        transition: background .16s ease, border-color .16s ease, box-shadow .16s ease, color .16s ease;
      }
      .st-hit:hover, .st-hit:focus-visible {
        border-color: var(--st-hit-ring) !important;
        box-shadow: inset 0 0 0 1px var(--st-hit-ring);
        background: var(--st-hit-bg) !important;
      }
      .st-hit-icon:hover:not(:disabled), .st-hit-icon:focus-visible:not(:disabled) {
        background: var(--st-hit-bg) !important;
        border-color: var(--st-hit-ring) !important;
        color: var(--st-hit-ink, inherit) !important;
      }
      .st-textlink { cursor: pointer; text-underline-offset: 3px; }
      .st-textlink:hover, .st-textlink:focus-visible { text-decoration: underline; }

      .st-ins .st-ins-open { opacity: 0.55; transition: opacity .16s ease }
      .st-ins:hover .st-ins-open { opacity: 1; }

      .st-brief-note { cursor: pointer; transition: background .16s ease; }
      .st-brief-note:hover, .st-brief-note:focus-visible { background: var(--st-hit-bg) !important; }
      .st-brief-note .st-ins-open { opacity: 0; transition: opacity .16s ease; }
      .st-brief-note:hover .st-ins-open, .st-brief-note:focus-visible .st-ins-open { opacity: 1; }
      .st-brief-notes { padding: 6px 8px 10px; }
      .st-brief-note + .st-brief-note { border-top: 1px solid var(--st-line, #DCE0E7); }
      @media (max-width: 720px) {
        .st-brief-note { grid-template-columns: 28px 56px 1fr !important; }
        .st-brief-stat { grid-column: 3; text-align: left !important; }
      }

      .st-chapter:hover .st-go {
        background: var(--st-acc);
        border-color: var(--st-acc) !important;
        color: #fff !important;
        transform: translateX(3px);
      }
      .st-go { transition: all .2s cubic-bezier(.16,1,.3,1) }

      .st-blk { transition: transform .18s cubic-bezier(.16,1,.3,1), filter .18s ease, box-shadow .18s ease, opacity .25s ease }
      .st-blk:hover { transform: translateY(-3px); filter: brightness(1.07); z-index: 3 }

      .st-scrubday .st-scrubbar { transition: background .22s ease, box-shadow .22s ease, transform .16s ease }
      .st-scrubday:hover:not(:disabled) .st-scrubbar {
        box-shadow: 0 0 0 3px var(--st-hit-ring);
        transform: scaleY(1.12);
      }
      .st-scrubday:focus-visible { outline: 2px solid var(--st-focus); outline-offset: 2px; border-radius: 6px }

      .st-approw { transition: background .16s ease, box-shadow .16s ease }
      .st-approw:hover, .st-approw:focus-visible {
        background: var(--st-hit-bg);
        box-shadow: inset 0 0 0 1px var(--st-hit-ring);
      }

      .st-rail { display: flex; flex-direction: column; }
      .st-day-tiles {
        display: grid;
        grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
        gap: 8px;
      }
      .st-sec-pair {
        display: grid;
        grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
        gap: 16px;
        align-items: stretch;
      }
      .st-sec-pair > section {
        min-width: 0;
        min-height: 0;
        height: 100%;
        max-height: min(480px, 62vh);
      }
      .st-sec-body { flex: 1; min-height: 0; overflow-y: auto; overflow-x: hidden; }
      .st-sec-body { scrollbar-width: thin; scrollbar-color: var(--st-sb) transparent }
      .st-sec-body::-webkit-scrollbar { width: 9px }
      .st-sec-body::-webkit-scrollbar-track { background: transparent }
      .st-sec-body::-webkit-scrollbar-thumb {
        background: var(--st-sb); border-radius: 99px;
        border: 3px solid transparent; background-clip: content-box;
      }
      @media (max-width: 960px) {
        .st-sec-pair { grid-template-columns: 1fr; }
        .st-sec-pair > section { height: auto; max-height: none; }
        .st-day-tiles { grid-template-columns: 1fr; }
      }
      .st-split { display: grid; grid-template-columns: 1.35fr 1fr; gap: 16px; align-items: start; }
      .st-split .st-chapters { grid-template-columns: 1fr !important; }
      @media (max-width: 900px) {
        .st-hero-band { grid-template-columns: 1fr !important; }
        .st-hero-band > div:first-child { border-right: none !important; border-bottom: 1px solid var(--st-sb); }
      }

      .st-drawer-body { scrollbar-width: thin; scrollbar-color: var(--st-sb) transparent }
      .st-drawer-body::-webkit-scrollbar { width: 9px }
      .st-drawer-body::-webkit-scrollbar-track { background: transparent }
      .st-drawer-body::-webkit-scrollbar-thumb {
        background: var(--st-sb); border-radius: 99px;
        border: 3px solid transparent; background-clip: content-box;
      }

      button.st-reset { border: none; background: transparent; font: inherit; color: inherit; text-align: left; padding: 0 }
      .st-focusable:focus-visible { outline: 2px solid var(--st-focus); outline-offset: 2px; border-radius: 8px }

      @media (max-width: 1060px) {
        .st-pulse { grid-template-columns: repeat(2, 1fr) !important }
        .st-chapters { grid-template-columns: 1fr !important }
        .st-split { grid-template-columns: 1fr !important }
        .st-rail { display: flex; flex-direction: column; }
        .st-hero-in { flex-wrap: wrap }
        .st-hero-right { margin-left: 0 !important }
      }
      @media (max-width: 620px) {
        .st-pulse { grid-template-columns: 1fr !important }
        .st-scrub { overflow-x: auto }
      }
      @media (prefers-reduced-motion: reduce) {
        .st-sk, .st-rise, .st-blk, .st-card-hover, .st-go, .st-ins-open { animation: none !important; transition: none !important }
        .st-card-hover:hover, .st-blk:hover { transform: none !important }
      }
    `}</style>
  );
}

/** CSS variables the classes above read; spread onto the page root. */
export function storyVars(t) {
  return {
    "--st-sk": t.isDark ? "#26282B" : "#E8EBF1",
    "--st-sk-hi": t.isDark ? "#303338" : "#F6F8FB",
    "--st-focus": t.c.ai,
    "--st-rowhover": t.isDark ? "rgba(255,255,255,.045)" : "rgba(24,32,46,.04)",
    "--st-hit-bg": t.isDark ? "rgba(255,255,255,.06)" : "rgba(66,115,209,.08)",
    "--st-hit-ring": t.isDark ? "rgba(120,159,232,.55)" : "rgba(66,115,209,.45)",
    "--st-hit-ink": t.c.text,
    "--st-sb": t.isDark ? "rgba(255,255,255,.16)" : "rgba(24,32,46,.18)",
    "--st-line": t.c.border,
  };
}

/* ── Skeleton block ────────────────────────────────────────────────────── */
export function Sk({ w = "100%", h = 14, r = 8, style }) {
  return <span aria-hidden="true" className="st-sk" style={{ display: "block", width: w, height: h, borderRadius: r, ...style }} />;
}

/* ── Animated counter ──────────────────────────────────────────────────── */
export function CountUp({ value = 0, suffix = "", duration = 1000, style }) {
  const [v, setV] = React.useState(0);
  React.useEffect(() => {
    let raf; const t0 = performance.now();
    const step = (now) => {
      const p = Math.min((now - t0) / duration, 1);
      setV(Math.round(value * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return <span style={style}>{v}{suffix}</span>;
}

/* ── Section frame ───────────────────────────────────────────────────────
   Heading + body are ONE card. The head is a well; the body is the rest.
   Do not render SectionHead on its own — it has no surface of its own. */
export function SectionFrame({ title, sub, kicker, meta, tone, children, footer }) {
  const t = useDashTheme();
  return (
    <section style={{
      margin: "20px 0 0",
      background: t.c.surface,
      border: `1px solid ${t.c.border}`,
      borderRadius: t.r.card,
      boxShadow: t.shadow.raised,
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      minHeight: 0,
    }}>
      <SectionHead title={title} sub={sub} kicker={kicker} meta={meta} tone={tone} />
      <div className="st-sec-body">{children}</div>
      {footer}
    </section>
  );
}

export function SectionHead({ title, sub, kicker, meta, tone }) {
  const t = useDashTheme();
  const ink = tone || t.c.primary;
  return (
    <div style={{
      padding: "12px 16px",
      borderBottom: `1px solid ${t.c.border}`,
      background: t.isDark ? t.c.surfaceMuted : "#F4F6FA",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: "8px 16px",
    }}>
      <div style={{ minWidth: 0 }}>
        {kicker && (
          <div style={{ ...t.font.statLabel, color: ink, marginBottom: 3 }}>{kicker}</div>
        )}
        <h2 style={{
          margin: 0, fontSize: 16, fontWeight: t.font.weight.semibold,
          letterSpacing: "-0.02em", lineHeight: 1.25, color: t.c.text,
        }}>{title}</h2>
      </div>
      <div style={{
        display: "flex", alignItems: "center", gap: 10, flex: "0 1 420px", justifyContent: "flex-end",
      }}>
        {sub && (
          <p style={{
            margin: 0, fontSize: 12.5, lineHeight: 1.4, textAlign: "right",
            color: t.c.textSecondary,
          }}>{sub}</p>
        )}
        {meta && (
          <span style={{
            flex: "none",
            fontSize: 12, fontWeight: 600, color: ink,
            background: t.isDark ? `${ink}26` : "#fff",
            border: `1px solid ${t.isDark ? `${ink}40` : t.c.border}`,
            borderRadius: t.r.pill, padding: "4px 10px",
            fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap",
          }}>{meta}</span>
        )}
      </div>
    </div>
  );
}

/* ── Deterministic day reading (no model involved) ─────────────────────── */

export function dayScore(totals) {
  if (!totals || !totals.trackedMin) return 0;
  const volume = Math.min(totals.trackedMin / 420, 1) * 45;         // 7h of tracked work = full marks
  const focus = (totals.focusPct / 100) * 35;
  const activity = (totals.activity / 100) * 20;
  return Math.max(1, Math.min(100, Math.round(volume + focus + activity)));
}

const CAT_PHRASE = {
  deep: "maker-heavy", review: "review-heavy", comms: "meeting-heavy",
  docs: "writing-heavy", browse: "research-heavy", distract: "scattered",
};

/** "a deep, maker-heavy Friday" — written from the measurements. */
export function dayPhrase(totals, threads, dateStr) {
  const weekday = (() => {
    const [y, m, d] = (dateStr || "").split("-").map(Number);
    return y ? new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: "long" }) : "day";
  })();
  if (!totals || !totals.trackedMin) return `a quiet ${weekday}`;
  const byCat = {};
  for (const th of threads || []) byCat[th.cat] = (byCat[th.cat] || 0) + th.minutes;
  const top = Object.entries(byCat).sort((a, b) => b[1] - a[1])[0];
  const shape = top && top[1] / totals.trackedMin >= 0.4 ? CAT_PHRASE[top[0]] : "mixed";
  const depth = totals.focusPct >= 60 ? "deep, " : totals.switchesPerHour >= 9 ? "busy, " : "";
  return `a ${depth}${shape} ${weekday}`;
}

/** One narrative sentence for the hero, deterministic and wording-safe. */
export function narrativeFor(name, totals, threads, apps) {
  if (!totals || !totals.trackedMin) {
    return `${name || "This person"} has nothing tracked on this day — neither the desktop app nor the background agent reported activity.`;
  }
  const first = (name || "").split(" ")[0] || "They";
  const top = (threads || [])[0];
  const topPart = top
    ? ` — the biggest share went to ${top.title}${(threads.length > 1) ? `, one of ${threads.length} threads` : ""}`
    : "";
  const runPart = totals.longestBlockMin
    ? ` Longest unbroken run was ${fmtShort(totals.longestBlockMin)} across ${apps?.length || 0} applications.`
    : ` ${apps?.length || 0} applications were in play.`;
  return `${first} put ${fmtShort(totals.trackedMin)} on the board${topPart}.${runPart}`;
}

/** Prose → scannable lines. The deep reads are asked for detail; detail reads
 *  best as one specific per line, not as a paragraph block. */
export function sentenceLines(text) {
  return String(text || "")
    .split(/(?<=[.!?])\s+(?=[A-Z0-9])/)
    .map((s) => s.replace(/\.$/, "").trim())
    .filter(Boolean);
}

/* ── Layered-reading primitives ─────────────────────────────────────────────
   The deep reads are organised for on-demand reading: a compact row you can
   scan, and — on click — that row's own detail layer (points, then the key
   insight). These three pieces keep every drawer's expansion identical. */

/** One specific per line, with a small tinted bullet. */
/* Every one of these renders inside the drawer, which is a reading surface, so
   they are set as body copy rather than captions. Previously 12px muted grey on
   a 1.55 leading with 5px between points: technically legible, genuinely hard to
   read a paragraph of. Four things move together —
     size      12 -> 14   (the same base the drawer body uses)
     colour    textSecondary -> text   (grey-on-grey was doing the most damage)
     leading   1.55 -> 1.7
     spacing   5 -> 11    so each point reads as its own line, not a wall
   and the line is capped near 68 characters, because an 840px drawer otherwise
   produces lines the eye loses its place on. */
export function PointLines({ points = [], tone, gap = 11 }) {
  const t = useDashTheme();
  const ink = t.c.primary;
  if (!points.length) return null;
  return (
    <span style={{ display: "block" }}>
      {points.map((pt, i) => (
        <span key={i} style={{ display: "flex", gap: 11, alignItems: "flex-start", marginTop: i ? gap : 0 }}>
          <span aria-hidden="true" style={{ width: 5, height: 5, borderRadius: 99, background: ink, flex: "none", marginTop: 9 }} />
          <span style={{ fontSize: t.font.size.base, color: t.c.text, lineHeight: 1.7, maxWidth: "68ch" }}>{pt}</span>
        </span>
      ))}
    </span>
  );
}

/** The "key insight" callout — the one thing worth remembering from a row. */
export function InsightCallout({ text }) {
  const t = useDashTheme();
  if (!text) return null;
  return (
    <span style={{
      display: "block", marginTop: 14, padding: "13px 16px", borderRadius: t.r.md,
      background: t.c.primarySoft,
      borderLeft: `3px solid ${t.c.primary}`,
    }}>
      <span style={{ ...t.font.statLabel, color: t.c.primary, display: "block", marginBottom: 5 }}>Key insight</span>
      <span style={{ fontSize: t.font.size.base, color: t.c.text, lineHeight: 1.65, maxWidth: "68ch", display: "block" }}>{text}</span>
    </span>
  );
}

/** Rotating chevron for accordion rows. */
export function ExpandChevron({ open }) {
  const t = useDashTheme();
  return (
    <span aria-hidden="true" style={{
      width: 24, height: 24, borderRadius: 8, flex: "none",
      display: "grid", placeItems: "center",
      border: `1px solid ${open ? t.c.primary : t.c.border}`,
      background: open ? t.c.primarySoft : "transparent",
      color: open ? t.c.primary : t.c.textTertiary, fontSize: 10,
      transform: open ? "rotate(90deg)" : "none",
      transition: "transform .22s cubic-bezier(.16,1,.3,1), border-color .18s ease, color .18s ease",
    }}>▶</span>
  );
}

export const fmtShort = (m) => {
  const n = Math.max(0, Math.round(m || 0));
  if (n < 60) return `${n}m`;
  const h = Math.floor(n / 60), r = n % 60;
  return r ? `${h}h ${r}m` : `${h}h`;
};

/* ── 14-day strip cache ────────────────────────────────────────────────────
   The hero scrubber shows a bar per day. There is no cheap endpoint for
   "tracked minutes for the last 14 days", so heights come from days this
   browser has already loaded (persisted per person+source). Unvisited days
   render as a neutral stub — honest, and it fills in as days are visited. */
const STRIP_KEY = "wsn-story-strip-v1";

function readStrip() {
  try { return JSON.parse(localStorage.getItem(STRIP_KEY) || "{}"); } catch (_) { return {}; }
}

export function stripGet(userId, source) {
  const all = readStrip();
  return (all[`${userId}|${source}`]) || {};
}

export function stripSet(userId, source, date, trackedMin) {
  try {
    const all = readStrip();
    const k = `${userId}|${source}`;
    all[k] = { ...(all[k] || {}), [date]: Math.round(trackedMin || 0) };
    // Keep each person's map small — the scrubber only ever shows 14 days.
    const entries = Object.entries(all[k]).sort((a, b) => (a[0] < b[0] ? 1 : -1)).slice(0, 40);
    all[k] = Object.fromEntries(entries);
    localStorage.setItem(STRIP_KEY, JSON.stringify(all));
  } catch (_) { /* cache only — never break the page for it */ }
}
